import { dateTimeHelpers } from '@approvalmax/utils';
import { useCallback, useMemo } from 'react';

import { DateRangeFilterRangeType } from '../../../../data/filters/dateRangeFilter';
import { optionsRangeType } from './FilterDateRange.constants';
import { FilterDateRangeProps } from './FilterDateRange.types';

export const useDateRangeFilter = (
    filter: FilterDateRangeProps['filter'],
    onChange: FilterDateRangeProps['onFilterChange']
) => {
    const { dateFrom, dateTo } = useMemo(
        () => ({
            dateFrom: filter.custom.greaterOrEqual ? new Date(filter.custom.greaterOrEqual) : undefined,
            dateTo: filter.custom.lessOrEqual ? new Date(filter.custom.lessOrEqual) : undefined,
        }),
        [filter.custom.greaterOrEqual, filter.custom.lessOrEqual]
    );

    const handleChangeRangeType = useCallback(
        (value: DateRangeFilterRangeType) => {
            onChange({
                ...filter,
                rangeType: value || DateRangeFilterRangeType.None,
            });
        },
        [filter, onChange]
    );

    const handleChangeGreaterOrEqual = useCallback(
        (value: Date | undefined) => {
            const greaterOrEqual = dateTimeHelpers.getMaxDate(value, filter.custom.lessOrEqual)?.toISOString() ?? null;

            if (greaterOrEqual !== filter.custom.greaterOrEqual) {
                onChange({
                    ...filter,
                    custom: {
                        ...filter.custom,
                        greaterOrEqual,
                    },
                });
            }
        },
        [filter, onChange]
    );

    const handleChangeLessOrEqual = useCallback(
        (value: Date | undefined) => {
            const lessOrEqual = dateTimeHelpers.getMinDate(value, filter.custom.greaterOrEqual)?.toISOString() ?? null;

            if (lessOrEqual !== filter.custom.lessOrEqual) {
                onChange({
                    ...filter,
                    custom: {
                        ...filter.custom,
                        lessOrEqual,
                    },
                });
            }
        },
        [filter, onChange]
    );

    const rangeType = useMemo(
        () => optionsRangeType.find((option) => option.id === filter.rangeType),
        [filter.rangeType]
    );

    return {
        dateFrom,
        dateTo,
        rangeType,
        handleChangeRangeType,
        handleChangeLessOrEqual,
        handleChangeGreaterOrEqual,
    };
};
